import type { FeatureKey, Features, Workspace } from "@baton/api";
import { isFeatureAvailable } from "@baton/ui";
import { createContext, useContext } from "react";

/**
 * FR-015-071, `contracts/screens.md` § Sidebar, § Feature screens: the one place availability
 * is decided. The `features` object of `GET /workspaces/{ws}` is read as-is; a key that is
 * absent or not `true` is unavailable, and nothing else is consulted, so an undelivered
 * feature's route, rail mode and sidebar rows are absent rather than disabled. `FeatureGate` and
 * the router both read `useFeatures()`.
 */
export function featuresOf(workspace: Workspace | undefined): Features {
  if (workspace === undefined) return {};
  const raw = workspace.features as Record<string, unknown>;
  const out: Partial<Record<FeatureKey, boolean>> = {};
  for (const [key, value] of Object.entries(raw)) if (value === true) out[key as FeatureKey] = true;
  return out;
}

const FeaturesContext = createContext<Features>({});

export const FeaturesProvider = FeaturesContext.Provider;

export function useFeatures(): Features {
  return useContext(FeaturesContext);
}

export function useFeature(feature: FeatureKey): boolean {
  return isFeatureAvailable(useFeatures(), feature);
}
